import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { locationMaster } from '../utils/locationMaster';
import {
  FaBuilding,
  FaLocationArrow,
  FaIdCard,
  FaGlobeAsia,
  FaAt,
  FaBarcode,
  FaUniversity,
  FaClipboardList,
  FaTrashAlt,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

const initialCustomerState = {
  company_id: '',
  company_name: '',
  address: '',
  pancard: '',
  city: '',
  state: '',
  country: '',
  gst_number: '',
  gst_uin: '',
  code: '',
  cin: '',
  email: '', // Only used for editing legacy, not for form state
  emails: [''],
  opening_balance: '',
  estimated_incoming: '',
  kyc_document_url: '',
};

type CustomerType = Omit<typeof initialCustomerState, 'emails' | 'email'> & {
  id?: string | number,
  emails: string[], // For UI state, but will send as 'email' string to server
  email?: string
};

type CompanyOption = {
  id: string | number;
  company_name: string;
}

const inputStyle =
  "flex items-center bg-white rounded-lg border border-gray-300 px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-colors shadow-sm w-full";
const labelStyle = "block mb-1 text-sm font-medium text-blue-900 tracking-wide";

const inputInnerStyle =
  "flex-1 outline-none bg-transparent w-full placeholder:truncate";
function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}


const AddCustomer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = searchParams.get('customer_id');
  const isEditMode = customerId && customerId !== 'new';

  const [customer, setCustomer] = useState<CustomerType>(initialCustomerState);
  const [companyList, setCompanyList] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [role, setRole] = useState<string | null>(null);
  const [loggedCompanyId, setLoggedCompanyId] = useState<string | null>(null);
  const [loggedCompanyName, setLoggedCompanyName] = useState<string | null>(null);
  const [uploadingKyc, setUploadingKyc] = useState(false);

  const handleKycUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadingKyc(true);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/auth/upload-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data && res.data.file_url) {
          setCustomer(prev => ({ ...prev, kyc_document_url: res.data.file_url }));
          toast.success("KYC document uploaded successfully.");
        }
      } catch (err) {
        toast.error("Failed to upload KYC document.");
      } finally {
        setUploadingKyc(false);
      }
    }
  };

  // On mount, fetch role and companies
  useEffect(() => {
    async function fetchInfo() {
      try {
      
        let token = localStorage.getItem("token");
        let decoded: any = null;
        if (token) decoded = decodeToken(token);
        const localRole = decoded.role;
        setRole(localRole);
        let companyId: string | null = null;
        if (decoded) {
          companyId =
            decoded.userId ||
            decoded.id ||
            decoded.companyId ||
            decoded.company_id ||
            decoded.cid ||
            null;
        }

        // Get all companies for dropdown and for mapping id->name
        const companyRes = await axios.get(`${API_URL}/auth/companies`);
        const companies: CompanyOption[] = Array.isArray(companyRes.data)
          ? companyRes.data.map((c: any) => ({
            id: c.id ?? c.company_id,
            company_name: c.company_name || c.name || '',
          }))
          : [];

        setCompanyList(companies);

        if (localRole === 'company' && companyId) {
          // Find own company
          const found = companies.find((c) => String(c.id) === String(companyId));
          setLoggedCompanyId(String(companyId));
          setLoggedCompanyName(found ? found.company_name : '');

          // Set company_id and company_name in customer state if adding new
          if (!isEditMode) {
            setCustomer((prev) => ({
              ...prev,
              company_id: String(companyId),
              company_name: found ? found.company_name : '',
            }));
          }
        }
      } catch (err) {
        setError('Could not fetch company info');
      }
    }
    fetchInfo();
    // eslint-disable-next-line
  }, [isEditMode]);

  // Fetch customer (edit mode)
  useEffect(() => {
    if (isEditMode && customerId) {
      setLoading(true);
      axios
        .get(`${API_URL}/auth/customer/${customerId}`)
        .then((res) => {
          let emails: string[] = [''];
          if (Array.isArray(res.data.emails)) {
            emails = res.data.emails.length > 0 ? res.data.emails : [''];
          } else if (typeof res.data.email === 'string' && res.data.email.includes(',')) {
            emails = res.data.email.split(',').map((e: string) => e.trim()).filter(Boolean);
          } else if (typeof res.data.email === 'string') {
            emails = res.data.email ? [res.data.email] : [''];
          } else if (typeof res.data.emails === 'string') {
            emails = res.data.emails ? res.data.emails.split(',').map((e: string) => e.trim()).filter(Boolean) : [''];
          }

          // Set both company_id and company_name on edit
          setCustomer({
            ...res.data,
            company_id: res.data.company_id ? String(res.data.company_id) : '',
            company_name: res.data.company_name || '',
            emails: emails,
          });
        })
        .catch(() => setError('Unable to load customer details.'))
        .finally(() => setLoading(false));
    } else {
      setCustomer((prev) => ({
        ...initialCustomerState,
        company_id: prev.company_id || '',
        company_name: prev.company_name || '',
      }));
    }
  }, [customerId, isEditMode]);

  // Company select handler (for non-company admins)
  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const selected = companyList.find(c => String(c.id) === selectedId);
    setCustomer((prev) => ({
      ...prev,
      company_id: selectedId,
      company_name: selected ? selected.company_name : '',
    }));
  };

  // For company-admins: company name is always set, but still allow editing (to display fresh edits)
  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomer((prev) => ({
      ...prev,
      company_name: value,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    // Only allow individual updating of non-array fields
    if (name === 'emails') return;
    setCustomer((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "country") {
        updated.state = "";
        updated.city = "";
      } else if (name === "state") {
        updated.city = "";
      }
      return updated;
    });
  };

  // Email field handlers
  const handleEmailChange = (index: number, value: string) => {
    setCustomer((prev) => {
      const updatedEmails = [...prev.emails];
      updatedEmails[index] = value;
      return { ...prev, emails: updatedEmails };
    });
  };

  const handleAddEmail = () => {
    setCustomer((prev) => ({
      ...prev,
      emails: [...(prev.emails || []), ''],
    }));
  };

  const handleRemoveEmail = (index: number) => {
    setCustomer((prev) => {
      let updatedEmails = [...prev.emails];
      updatedEmails.splice(index, 1);
      // Always keep at least one email field to avoid empty array
      if (updatedEmails.length === 0) updatedEmails = [''];
      return { ...prev, emails: updatedEmails };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccess('');

    // Validate company_id (required)
    if (!customer.company_id) {
      setFormError('Please select a company.');
      return;
    }
    if (!customer.company_name.trim()) {
      setFormError('Company name is required.');
      return;
    }
    // Email validation: at least 1 valid non-empty email string
    const cleanedEmails = customer.emails.map(e => e.trim()).filter(Boolean);
    if (cleanedEmails.length === 0) {
      setFormError('At least one email is required.');
      return;
    }

    setSubmitting(true);
    try {
      // Send 'email' as a comma separated string to the server
      const customerToSend: any = {
        ...customer,
        email: cleanedEmails.join(','),
        company_id: customer.company_id,
      };
      // Remove 'emails' key before sending
      delete customerToSend.emails;

      if (isEditMode && customerId) {
        // PUT (update)
        await axios.put(`${API_URL}/auth/customer/${customerId}`, customerToSend);
        toast.success('Customer updated successfully');
      } else {
        // POST (create)
        await axios.post(`${API_URL}/auth/customer`, customerToSend);
        toast.success('Customer added successfully');
        // Redirect to customers page after creation
        navigate(`/customer`);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Error saving customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/auth/customer/${customerId}`);
      setShowDeleteConfirm(false);
      toast.success('Customer deleted successfully.');
      setTimeout(() => navigate('/customer'), 1200);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Error deleting customer');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto mt-16 p-16 bg-gradient-to-br from-blue-100 via-pink-100 to-indigo-100 rounded-[3rem] shadow-2xl border-2 border-blue-200 animate__animated animate__fadeIn">
      <h2 className="text-4xl font-extrabold mb-10 text-blue-900 tracking-wide flex items-center gap-4">
        <FaBuilding className="inline text-blue-500 drop-shadow-md" size={40} />
        {isEditMode ? 'Edit Customer' : 'Add Customer'}
      </h2>
      {/* Toast notifications handle error and success messages */}
      <form onSubmit={handleSubmit} className="space-y-10">
        {/* Company select and Customer name & Emails */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

          {/* Company */}
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaBuilding className="text-indigo-500" size={26} />
                Company <span className="text-red-600">*</span>
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              {/* Company FIELD LOGIC: */}
              {role === 'company' ? (
                <input
                  type="text"
                  name="company_name"
                  className={inputInnerStyle + " font-semibold text-lg py-4 px-5 rounded-xl"}
                  value={loggedCompanyName || customer.company_name}
                  disabled
                  readOnly
                  placeholder="Company"
                />
              ) : (
                <select
                  name="company_id"
                  className={inputInnerStyle + " font-medium text-lg py-4 px-5 rounded-xl"}
                  value={customer.company_id}
                  onChange={handleCompanyChange}
                  required
                  disabled={loading || submitting}
                >
                  <option value="">Select company</option>
                  {companyList.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Customer Name */}
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaBuilding className="text-blue-400" size={26} /> Customer Name <span className="text-red-600">*</span>
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <input
                type="text"
                name="company_name"
                className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl"}
                value={customer.company_name}
                onChange={handleCompanyNameChange}
                required
                autoFocus
                disabled={loading || submitting}
                placeholder="Ex: Acme Pvt Ltd"
                maxLength={64}
              />
            </div>
          </div>
        </div>

        {/* Emails, Opening Balance & Estimated Incoming */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Opening Balance */}
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaUniversity className="text-green-500" size={24} /> Opening Balance (₹)
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <input
                type="number"
                name="opening_balance"
                step="any"
                className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl"}
                value={customer.opening_balance}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="0.00"
              />
            </div>
          </div>
          {/* Estimated Incoming */}
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaUniversity className="text-blue-500" size={24} /> Estimated Incoming (₹)
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <input
                type="number"
                name="estimated_incoming"
                step="any"
                className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl"}
                value={customer.estimated_incoming}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaAt className="text-indigo-400" size={24} /> Email(s)
              </span>
            </label>
            <div className="space-y-3">
              {customer.emails.map((emaildata, idx) => (
                <div key={idx} className={`flex items-center gap-3 mb-1 ${inputStyle} py-2`}>
                  <input
                    type="email"
                    name={`email_${idx}`}
                    className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl flex-1"}
                    value={emaildata}
                    onChange={e => handleEmailChange(idx, e.target.value)}
                    disabled={loading || submitting}
                    placeholder="customer@email.com"
                    maxLength={48}
                  />
                  {customer.emails.length > 1 && (
                    <button
                      type="button"
                      title="Remove this email"
                      onClick={() => handleRemoveEmail(idx)}
                      tabIndex={-1}
                      className="ml-2 text-red-600 hover:text-red-800 px-3 py-2 rounded-lg transition text-2xl bg-red-100 hover:bg-red-200 shadow"
                      disabled={loading || submitting}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="mt-2 ml-1 px-5 py-3 rounded-xl bg-blue-100 text-blue-700 border-2 border-blue-200 hover:bg-blue-200 text-lg font-semibold transition shadow"
                onClick={handleAddEmail}
                disabled={loading || submitting || customer.emails.length >= 5}
                tabIndex={0}
              >
                + Add Email
              </button>
            </div>
          </div>
        </div>

        {/* Address */}
        <div>
          <label className={labelStyle + " text-lg mb-3"}>
            <span className="flex items-center gap-3">
              <FaLocationArrow className="text-pink-400" size={24} /> Address
            </span>
          </label>
          <div className={inputStyle + " py-3"}>
            <textarea
              name="address"
              className={inputInnerStyle + " resize-none text-lg py-4 px-5 rounded-xl"}
              value={customer.address}
              onChange={handleInputChange}
              rows={4}
              disabled={loading || submitting}
              placeholder="Full address"
              maxLength={120}
            />
          </div>
        </div>

        {/* PAN, GST Number, GST UIN, CIN - 2 rows, 4 cols */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaIdCard className="text-yellow-500" size={22} /> PAN
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <input
                type="text"
                name="pancard"
                className={inputInnerStyle + " uppercase text-lg py-4 px-5 rounded-xl"}
                value={customer.pancard}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="ABCDE1234F"
                maxLength={10}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaBarcode className="text-green-500" size={22} /> GST Number
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <input
                type="text"
                name="gst_number"
                className={inputInnerStyle + " uppercase text-lg py-4 px-5 rounded-xl"}
                value={customer.gst_number}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaClipboardList className="text-indigo-400" size={22} /> GST UIN
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <input
                type="text"
                name="gst_uin"
                className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl"}
                value={customer.gst_uin}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="Optional"
                maxLength={16}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaUniversity className="text-teal-500" size={22} /> CIN
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <input
                type="text"
                name="cin"
                className={inputInnerStyle + " uppercase text-lg py-4 px-5 rounded-xl"}
                value={customer.cin}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="L12345MH2012PLC123456"
                maxLength={21}
              />
            </div>
          </div>
        </div>

        {/* City, State, Country, Code - 2 rows, 4 cols */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaGlobeAsia className="text-pink-400" size={22} /> Country
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <select
                name="country"
                className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl bg-transparent"}
                value={customer.country}
                onChange={handleInputChange}
                disabled={loading || submitting}
              >
                <option value="">-- Choose Country --</option>
                {Object.keys(locationMaster).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaLocationArrow className="text-purple-500" size={22} /> State
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <select
                name="state"
                className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl bg-transparent"}
                value={customer.state}
                onChange={handleInputChange}
                disabled={loading || submitting || !customer.country}
              >
                <option value="">-- Choose State --</option>
                {customer.country && Object.keys(locationMaster[customer.country] || {}).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaBuilding className="text-blue-500" size={22} /> City
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <select
                name="city"
                className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl bg-transparent"}
                value={customer.city}
                onChange={handleInputChange}
                disabled={loading || submitting || !customer.state}
              >
                <option value="">-- Choose City --</option>
                {customer.country && customer.state && (locationMaster[customer.country]?.[customer.state] || []).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelStyle + " text-lg mb-3"}>
              <span className="flex items-center gap-3">
                <FaBarcode className="text-cyan-500" size={22} /> Code
              </span>
            </label>
            <div className={inputStyle + " py-3"}>
              <input
                type="text"
                name="code"
                className={inputInnerStyle + " text-lg py-4 px-5 rounded-xl"}
                value={customer.code}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="Internal code"
                maxLength={16}
              />
            </div>
          </div>
        </div>

        {/* KYC Document Upload */}
        <div>
          <label className={labelStyle + " text-lg mb-3"}>
            <span className="flex items-center gap-3">
              <FaClipboardList className="text-indigo-400" size={24} /> KYC Document (PDF or Image)
            </span>
          </label>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className={inputStyle + " py-3 max-w-md"}>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={handleKycUpload}
                disabled={loading || submitting || uploadingKyc}
                className={inputInnerStyle + " text-lg py-1 px-2 rounded-xl cursor-pointer"}
              />
            </div>
            {uploadingKyc && <span className="text-sm font-semibold text-blue-600 animate-pulse">Uploading KYC...</span>}
            {customer.kyc_document_url && (
              <a
                href={customer.kyc_document_url.startsWith('http') ? customer.kyc_document_url : `${API_URL}${customer.kyc_document_url.startsWith('/') ? '' : '/'}${customer.kyc_document_url}`}
                target="_blank"
                rel="noreferrer"
                className="px-5 py-3 bg-indigo-100 text-indigo-700 border-2 border-indigo-200 hover:bg-indigo-200 text-lg font-semibold rounded-xl transition shadow"
              >
                📎 View Current KYC
              </a>
            )}
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex flex-wrap gap-8 mt-12 items-center">
          <button
            type="submit"
            className={`flex items-center gap-4 px-10 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white text-2xl rounded-2xl font-bold hover:from-blue-800 hover:to-indigo-800 transition disabled:opacity-60 shadow-2xl ${submitting ? 'cursor-not-allowed' : ''}`}
            disabled={submitting || loading}
          >
            {submitting
              ? isEditMode
                ? 'Saving...'
                : 'Adding...'
              : isEditMode
                ? 'Save Changes'
                : 'Add Customer'}
          </button>
          {isEditMode && (
            <>
              <button
                type="button"
                className="flex items-center gap-4 px-10 py-4 bg-gradient-to-r from-red-500 to-pink-500 text-white text-2xl rounded-2xl font-bold hover:from-red-600 hover:to-red-700 transition shadow-2xl"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteLoading}
              >
                <FaTrashAlt className="inline" size={24} /> Delete
              </button>
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 transition">
                  <div className="bg-white rounded-2xl shadow-2xl p-12 w-[540px] space-y-8 border-4 border-red-200 animate__animated animate__fadeIn">
                    <div className="font-bold text-2xl text-red-700 flex gap-3 items-center"><FaTrashAlt size={28}/> Delete Customer</div>
                    <div className="text-lg text-gray-700 leading-relaxed">
                      Are you sure you want to delete this customer?<br />
                      <b>This will also delete all related bank details.</b>
                    </div>
                    <div className="flex justify-end space-x-6">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-6 py-3 rounded-lg border-2 font-bold text-xl hover:bg-gray-100 transition"
                        disabled={deleteLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        className={`px-6 py-3 rounded-lg bg-gradient-to-r from-red-700 to-pink-700 text-white font-bold text-xl shadow-xl ${deleteLoading ? 'opacity-60' : ''}`}
                        disabled={deleteLoading}
                      >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddCustomer;